import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Trophy, TrendingUp } from 'lucide-react';

interface StatsDataType {
  athleteName?: string;
  athleteAvatar?: string;
  gradYear?: number | string;
  sport?: string;
  position?: string;
  stats: {
    name: string;
    value: string;
    category?: string;
  }[];
}

interface StatsCardProps {
  statsData: StatsDataType;
}

const StatsCard: React.FC<StatsCardProps> = ({ statsData }) => {
  const getInitials = (name: string | undefined) => {
    if (!name) return 'A';
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Group stats by category
  const groupedStats = statsData.stats.reduce((acc, stat) => {
    const category = stat.category || 'General';
    if (!acc[category]) acc[category] = [];
    acc[category].push(stat);
    return acc;
  }, {} as Record<string, typeof statsData.stats>);

  return (
    <div className="bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border border-primary/20 rounded-xl p-4 overflow-hidden">
      {/* Header with athlete info */}
      <div className="flex items-center gap-3 mb-4">
        <Avatar className="h-12 w-12 ring-2 ring-primary/30">
          <AvatarImage src={statsData.athleteAvatar || undefined} alt={statsData.athleteName} />
          <AvatarFallback className="bg-primary text-primary-foreground font-semibold">
            {getInitials(statsData.athleteName)}
          </AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-foreground truncate">{statsData.athleteName || 'Athlete'}</h3>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {statsData.sport && <span>{statsData.sport}</span>}
            {statsData.sport && statsData.position && <span>•</span>}
            {statsData.position && <span>{statsData.position}</span>}
            {(statsData.sport || statsData.position) && statsData.gradYear && <span>•</span>}
            {statsData.gradYear && <span>Class of {statsData.gradYear}</span>}
          </div>
        </div>
        <Trophy className="h-6 w-6 text-primary/60" />
      </div>

      {/* Stats Grid */}
      <div className="space-y-3">
        {Object.entries(groupedStats).map(([category, categoryStats]) => (
          <div key={category}>
            {Object.keys(groupedStats).length > 1 && (
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                  {category}
                </span>
              </div>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {categoryStats.map((stat, index) => (
                <div
                  key={index}
                  className="bg-background/60 rounded-lg p-3 text-center"
                >
                  <div className="text-xl font-bold text-foreground">{stat.value}</div>
                  <div className="text-xs text-muted-foreground truncate">{stat.name}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer branding */}
      <div className="mt-4 pt-3 border-t border-primary/10 flex items-center justify-center">
        <span className="text-xs text-muted-foreground/60">Shared via RecruitLook</span>
      </div>
    </div>
  );
};

export default StatsCard;
